import { useState, type FormEvent } from 'react'
import { ApiError } from '@/api/errors'
import type { Order, Priority } from '@/api/types'
import { priorityLabel } from '@/i18n/labels'
import { useRouteTemplatesList } from '@/features/refs/queries'
import { Button, Field, Input, Select, Sheet } from '@/ui'
import { useUpdateOrder } from './queries'

const PRIORITIES: Priority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT']

/** Convert an ISO timestamp to the value a datetime-local input expects. */
function toLocalInput(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function OrderEditSheet({
  order,
  open,
  onClose,
}: {
  order: Order
  open: boolean
  onClose: () => void
}) {
  const update = useUpdateOrder(order.id)
  const routes = useRouteTemplatesList()
  const [clientName, setClientName] = useState(order.client_name)
  const [productName, setProductName] = useState(order.product_name)
  const [dueAt, setDueAt] = useState(toLocalInput(order.due_at))
  const [quantity, setQuantity] = useState(String(order.quantity))
  const [priority, setPriority] = useState<Priority>(order.priority)
  const [routeId, setRouteId] = useState(order.route_template_id ?? '')

  const activeRoutes = (routes.data?.items ?? []).filter(
    (r) => r.is_active || r.id === order.route_template_id,
  )
  const errMessage = update.error instanceof ApiError ? update.error.message : null

  function submit(e: FormEvent) {
    e.preventDefault()
    update.mutate(
      {
        version: order.version,
        client_name: clientName.trim(),
        product_name: productName.trim(),
        due_at: dueAt ? new Date(dueAt).toISOString() : order.due_at,
        quantity: Number(quantity) || 1,
        priority,
        route_template_id: routeId || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Sheet open={open} onClose={onClose} title="Редактировать заказ">
      <form onSubmit={submit} className="max-h-[70vh] overflow-y-auto">
        <Field label="Клиент">
          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} />
        </Field>
        <Field label="Изделие">
          <Input value={productName} onChange={(e) => setProductName(e.target.value)} />
        </Field>
        <Field label="Срок">
          <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Количество">
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Field>
          <Field label="Приоритет">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {priorityLabel[p]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Маршрут">
          <Select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">— не выбран —</option>
            {activeRoutes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
        {errMessage && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red">{errMessage}</p>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" fullWidth loading={update.isPending}>
            Сохранить
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
